import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { RequireAuth } from '@/auth/require-auth'
import { AdminLayout } from '@/components/layout/admin-layout'
import { InstructorLayout } from '@/components/layout/instructor-layout'
import { LearnerLayout } from '@/components/layout/learner-layout'
import { PublicLayout } from '@/components/layout/public-layout'
import { RouteErrorBoundary } from '@/components/layout/route-error-boundary'
import { RouteHydrateFallback } from '@/components/layout/route-hydrate-fallback'

const lazyPage = (
  importer: () => Promise<{ default: React.ComponentType }>,
) => ({
  lazy: async () => ({ Component: (await importer()).default }),
})

// Every route below is lazy-loaded, so on a cold/hard-reloaded page there is
// no route component to render yet while that chunk is being fetched.
// Without this, react-router renders nothing (a blank flash) and logs a
// "No HydrateFallback element provided" warning; both top-level branches
// need their own since either can be the first-matched route for a URL.
export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    HydrateFallback: RouteHydrateFallback,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/', ...lazyPage(() => import('@/pages/home-page')) },
      { path: '/courses', ...lazyPage(() => import('@/pages/catalog-page')) },
      { path: '/blog', ...lazyPage(() => import('@/pages/blog-page')) },
      {
        path: '/instructors',
        ...lazyPage(() => import('@/pages/instructors-page')),
      },
      {
        path: '/physical-classes',
        ...lazyPage(() => import('@/pages/physical-classes-page')),
      },
      {
        path: '/wishlist',
        ...lazyPage(() => import('@/pages/wishlist-page')),
      },
      { path: '/terms', ...lazyPage(() => import('@/pages/terms-page')) },
      { path: '/privacy', ...lazyPage(() => import('@/pages/privacy-page')) },
      { path: '/contact', ...lazyPage(() => import('@/pages/contact-page')) },
      {
        path: '/courses/:slug',
        ...lazyPage(() => import('@/pages/course-detail-page')),
      },
      { path: '/login', ...lazyPage(() => import('@/pages/auth/login-page')) },
      {
        path: '/register',
        ...lazyPage(() => import('@/pages/auth/register-page')),
      },
      {
        path: '/forgot-password',
        ...lazyPage(() => import('@/pages/auth/forgot-password-page')),
      },
      {
        path: '/reset-password',
        ...lazyPage(() => import('@/pages/auth/reset-password-page')),
      },
      {
        path: '/accept-invite',
        ...lazyPage(() => import('@/pages/auth/accept-invite-page')),
      },
      {
        path: '/verify/:certificateNo?',
        ...lazyPage(() => import('@/pages/verify-certificate-page')),
      },
      {
        // requireVerified={false}: this IS the verification gate — an
        // unverified user must be able to reach it, or they'd be stuck
        // bouncing between "go verify" and "can't load verify page".
        element: (
          <RequireAuth
            roles={['learner', 'instructor', 'admin']}
            requireVerified={false}
          >
            <Outlet />
          </RequireAuth>
        ),
        children: [
          {
            path: '/verify-otp',
            ...lazyPage(() => import('@/pages/auth/verify-otp-page')),
          },
        ],
      },
      { path: '*', ...lazyPage(() => import('@/pages/not-found-page')) },
    ],
  },
  // Every dashboard-shell area below is outside PublicLayout on purpose:
  // DashboardShell (sidebar nav, mobile menu, logout) is already a complete
  // layout on its own — stacking the marketing SiteHeader/SiteFooter around
  // it means two competing navs on one screen. Same reasoning as the course
  // player just below, extended to the rest of the logged-in app.
  {
    element: (
      <RequireAuth roles={['learner', 'instructor', 'admin']}>
        <LearnerLayout />
      </RequireAuth>
    ),
    HydrateFallback: RouteHydrateFallback,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/dashboard',
        ...lazyPage(() => import('@/pages/learner/dashboard-page')),
      },
      {
        path: '/certificates',
        ...lazyPage(() => import('@/pages/learner/certificates-page')),
      },
      {
        path: '/certificates/:id',
        ...lazyPage(() => import('@/pages/learner/certificate-detail-page')),
      },
      {
        path: '/profile',
        ...lazyPage(() => import('@/pages/profile-page')),
      },
    ],
  },
  {
    element: (
      <RequireAuth roles={['learner']}>
        <LearnerLayout />
      </RequireAuth>
    ),
    HydrateFallback: RouteHydrateFallback,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/cart',
        ...lazyPage(() => import('@/pages/learner/cart-page')),
      },
      {
        path: '/orders',
        ...lazyPage(() => import('@/pages/learner/orders-page')),
      },
    ],
  },
  {
    path: '/instructor',
    element: (
      <RequireAuth roles={['instructor']}>
        <InstructorLayout />
      </RequireAuth>
    ),
    HydrateFallback: RouteHydrateFallback,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      {
        path: 'dashboard',
        ...lazyPage(
          () => import('@/pages/instructor/instructor-dashboard-page'),
        ),
      },
      {
        path: 'courses',
        ...lazyPage(
          () => import('@/pages/instructor/instructor-courses-page'),
        ),
      },
      {
        path: 'courses/:id/edit',
        ...lazyPage(
          () =>
            import('@/pages/instructor/course-builder/course-builder-page'),
        ),
      },
      {
        path: 'students',
        ...lazyPage(
          () => import('@/pages/instructor/instructor-students-page'),
        ),
      },
      {
        path: 'assignments',
        ...lazyPage(
          () => import('@/pages/instructor/instructor-assignments-page'),
        ),
      },
      {
        path: 'discussions',
        ...lazyPage(
          () => import('@/pages/instructor/instructor-discussions-page'),
        ),
      },
      {
        path: 'analytics',
        ...lazyPage(
          () => import('@/pages/instructor/instructor-analytics-page'),
        ),
      },
    ],
  },
  {
    path: '/admin',
    element: (
      <RequireAuth roles={['admin']}>
        <AdminLayout />
      </RequireAuth>
    ),
    HydrateFallback: RouteHydrateFallback,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      {
        path: 'dashboard',
        ...lazyPage(() => import('@/pages/admin/admin-dashboard-page')),
      },
      {
        path: 'users',
        ...lazyPage(() => import('@/pages/admin/admin-users-page')),
      },
      {
        path: 'users/:id',
        ...lazyPage(() => import('@/pages/admin/admin-user-detail-page')),
      },
      {
        path: 'courses',
        ...lazyPage(() => import('@/pages/admin/admin-courses-page')),
      },
      {
        path: 'courses/:id',
        ...lazyPage(() => import('@/pages/admin/admin-course-review-page')),
      },
      {
        path: 'categories',
        ...lazyPage(() => import('@/pages/admin/admin-categories-page')),
      },
      {
        path: 'enrollments',
        ...lazyPage(() => import('@/pages/admin/admin-enrollments-page')),
      },
      {
        path: 'payments',
        ...lazyPage(() => import('@/pages/admin/admin-payments-page')),
      },
      {
        path: 'analytics',
        ...lazyPage(() => import('@/pages/admin/admin-analytics-page')),
      },
      {
        path: 'audit-log',
        ...lazyPage(() => import('@/pages/admin/admin-audit-log-page')),
      },
    ],
  },
  // Outside PublicLayout on purpose: the course player renders its own
  // full-viewport header/sidebar (see course-player-page.tsx) — stacking the
  // marketing SiteHeader/SiteFooter around it clashes with that layout.
  // Instructors/admins land here too, in read-only "preview" mode (see
  // course-player-page.tsx's isPreview branch) — never enrolled, so the
  // page fetches a different, ownership-gated backend endpoint for them.
  {
    element: (
      <RequireAuth roles={['learner', 'instructor', 'admin']}>
        <Outlet />
      </RequireAuth>
    ),
    HydrateFallback: RouteHydrateFallback,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/learn/:courseId',
        ...lazyPage(() => import('@/pages/learner/course-player-page')),
      },
    ],
  },
])
